import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";

import BaseDialog from "@/components/Modals/BaseDialog";
import { NoxusInputField } from "@/components/Form/InputField";
import { Label } from "@/components/shadcn/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { SpotToast } from "@/components/SpotToast";
import { TablesService } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useDialogMutationLoading } from "@/hooks/useDialogMutationLoading";
import { IdentifierField, toSnakeCase } from "./IdentifierField";
import { MAX_ENTITY_NAME } from "@/constants/entityLimits";

type IdType = "uuid" | "autoincrement";

type FormData = {
  name: string;
  idType: IdType;
};

export function CreateTableDialog({
  isOpen,
  onOpenChange,
  // Open the new table's detail page after creating. The schema builder turns
  // this off so creating a table just drops a node on the canvas.
  navigate = true,
  onCreated,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  navigate?: boolean;
  onCreated?: (tableId: string) => void;
}) {
  const { spotGroup } = useSpotUser();
  const router = useRouter();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: { name: "", idType: "uuid" },
  });

  const nameValue = watch("name");
  const identifier = toSnakeCase(nameValue);

  const { data: existingTables } = useRemoteData(
    DATA_KEYS.TABLES,
    { path: { group_id: spotGroup.id } },
    { enabled: isOpen }
  );
  const nameTaken =
    nameValue.trim().length > 0 &&
    (existingTables ?? []).some(
      (t) => t.name.trim().toLowerCase() === nameValue.trim().toLowerCase()
    );

  const createMutation = useMutation({
    mutationFn: TablesService.createTable,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.TABLES] });
      onOpenChange(false);
      onCreated?.(res.data.id);
      if (navigate) router.push(`/tables/${res.data.id}`);
    },
    onError: () => SpotToast.error("Failed to create table"),
  });

  const onSubmit = (data: FormData) => {
    if (nameTaken) return;
    createMutation.mutate({
      path: { group_id: spotGroup.id },
      body: { name: data.name.trim(), id_type: data.idType, columns: [] },
    });
  };

  const isSaving = useDialogMutationLoading(isOpen, createMutation);

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="New table"
      description="Create a table to store structured data."
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isSaving,
          },
        },
        {
          label: "Create table",
          props: {
            onClick: handleSubmit(onSubmit),
            type: "submit",
            disabled: !isValid || nameTaken,
            loading: isSaving,
          },
        },
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <NoxusInputField
            name="name"
            label="Name"
            control={control}
            errors={errors}
            placeholder="e.g. Customers"
            autoFocus
            maxLength={MAX_ENTITY_NAME}
            helperText={
              nameTaken ? (
                <span className="text-destructive">
                  A table with this name already exists.
                </span>
              ) : (
                "Used as the display name. A SQL-safe identifier is generated automatically."
              )
            }
            rules={{
              required: { value: true, message: "Table name is required." },
              validate: (value: string) =>
                value.trim().length > 0 || "Table name is required.",
            }}
          />

          {identifier && <IdentifierField identifier={identifier} />}

          <Accordion type="single" collapsible className="-mx-1">
            <AccordionItem value="advanced" className="border-b-0">
              <AccordionTrigger className="px-1 py-2 text-sm font-medium hover:no-underline">
                Advanced
              </AccordionTrigger>
              <AccordionContent className="px-1 pb-1 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="id-type">Primary key</Label>
                  <Controller
                    name="idType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(v) => field.onChange(v as IdType)}
                      >
                        <SelectTrigger id="id-type" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="uuid">UUID (random)</SelectItem>
                          <SelectItem value="autoincrement">
                            Auto-increment (1, 2, 3…)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    How each row&apos;s identifier is generated. This can&apos;t
                    be changed later.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </form>
    </BaseDialog>
  );
}
